import { type CancelToken } from 'axios';
import type { DeepWritable } from 'ts-essentials';
import { SelectedDataType } from '~/_shared/components/customizeMetrics/selectedDataMetric.type';
import type {
  PerColumn, SpreadsheetColumn,
} from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { isColumnNumericFromNumberResponse } from '~/_shared/utils/spreadsheet/useIsNumericColumnCheck.helpers';
import { type BoundaryFilterRequest } from '~/spreadsheet/filter/boundary/spreadsheetFilterBoundary.types';
import { type BoundaryTerritoryFilterRequest } from '~/spreadsheet/filter/boundaryTerritory/spreadsheetFilterBoundaryTerritory.types';
import { type PolygonFilterRequest } from '~/spreadsheet/filter/polygon/spreadsheetPolygonFilter.types';
import { type RadiusFilterRequest } from '~/spreadsheet/filter/radius/spreadsheetFilterRadius.types';
import {
  getSpreadsheetBulkData, type SpreadsheetDataBulkRequestGetter, type SpreadsheetDataColumnsToFetch,
} from '~/spreadsheet/spreadsheet.repository';
import { isSuccessBulkSpreadsheet } from '~/spreadsheet/typeguards';
import {
  getAgsDemographicsQuery, getDemographicsQuery,
} from '~/store/demographics/demographics.repository';
import {
  DEFAULT_DEMOGRAPHIC_REGION, type DemographicRegion,
} from '~/store/demographics/demographics.types';
import {
  createFilterTreeRequestFromMapSettings, type FilterTreeRequest, mergeFilterTrees,
} from '~/store/spreadsheetData/filtering/spreadsheetDataFiltering.helpers';
import { type FilterTreeMapSettingsParams } from '~/store/spreadsheetData/filtering/useFilterTreeMapSettingsParams';
import { createSearchFilterTreeRequestFromMapSettings } from '~/store/spreadsheetData/search/spreadsheetData.search.helpers';
import { DataType } from '~/store/spreadsheetData/spreadsheetData.state';
import { SearchMatchingBehaviour } from '../../constants/searchMatchingBehaviour.enum';
import {
  newPerSpreadsheetMap, type PerSpreadsheet,
} from '../../types/spreadsheet/spreadsheet.types';
import {
  isNullOrUndefined, notNull,
} from '../typeGuards';
import { type DataLayerAreaValidationResult } from './dataLayer/validateDataLayerArea';
import { type DemographicMetricModel } from './demographicsMetric.factory';
import { type MetricModel } from './metrics.factory';
import {
  type MetricProps, MetricsDataAction,
} from './metrics.types';
import { type SpreadsheetColumnMetricModel } from './spreadsheetMetric.factory';

export const METRICS_EXCLUDED_ROWS_COUNT = 'METRICS_EXCLUDED_ROWS_COUNT';
export const METRICS_ROWS_COUNT = 'METRICS_ROWS_COUNT';

const numericMetricsTypes = [MetricsDataAction.AVERAGE, MetricsDataAction.HIGH_VALUE, MetricsDataAction.LOW_VALUE, MetricsDataAction.SUM];
export const perColumnGroupedMetric = [MetricsDataAction.GROUP_COUNT, MetricsDataAction.GROUP_TOTAL];

type GetMetricsDataArguments = {
  clientId: number;
  mapId: number;
  cancelToken: CancelToken;
  selectedMetrics: ReadonlyArray<MetricModel>;
  filterTreeParams?: FilterTreeMapSettingsParams;
  radiusFilterRequest?: RadiusFilterRequest;
  polygonFilterRequest?: PolygonFilterRequest;
  boundaryFilterRequest?: BoundaryFilterRequest;
  boundaryTerritoryFilterRequest?: BoundaryTerritoryFilterRequest;
  spreadsheetRowsCount?: ReadonlyArray<SpreadsheetId>;
  validateDataLayerArea?: () => Promise<DataLayerAreaValidationResult>;
};

export type SpreadsheetMetricProps = {
  rowsCount: number | null;
  metricList: MetricProps[];
};

export type MetricsResults = {
  demographicMetrics: MetricProps[];
  spreadsheetColumnMetrics: SpreadsheetMetricProps;
};

type DemographicMetricsResults = Readonly<{
  metrics: MetricProps[];
  isAgsError: boolean;
  dataLayerAreaErrorCode: string | null;
}>;

export const getAndProcessMetricsData = async (metricsArguments: GetMetricsDataArguments): Promise<MetricsResults & Omit<DemographicMetricsResults, 'metrics'>> => {
  const { metrics: demographicMetrics, isAgsError, dataLayerAreaErrorCode } = await getDemographicsMetrics(metricsArguments);
  const spreadsheetColumnMetrics = await getSpreadsheetColumnMetricsData(metricsArguments);

  return {
    demographicMetrics,
    spreadsheetColumnMetrics,
    isAgsError,
    dataLayerAreaErrorCode,
  };
};

const isDataLayerMetricInRegions = (metric: DemographicMetricModel, regions: ReadonlyArray<DemographicRegion>) =>
  metric.inferenceSource?.type === 'DataLayers' && regions.includes(metric.inferenceSource.region);

const getDemographicsMetrics = async ({ clientId, mapId, selectedMetrics, radiusFilterRequest, cancelToken,
  boundaryFilterRequest, boundaryTerritoryFilterRequest, polygonFilterRequest, validateDataLayerArea }: GetMetricsDataArguments): Promise<DemographicMetricsResults> => {
  const demographicMetrics = selectedMetrics
    .filter((metric): metric is DemographicMetricModel => metric.type === SelectedDataType.Demographic);

  if (demographicMetrics.length === 0) {
    return { metrics: [], isAgsError: false, dataLayerAreaErrorCode: null };
  }

  let isAgsError = false;
  let dataLayerAreaErrorCode: string | null = null;
  const responses = await Promise.all([false, true].map(async isAgs => {
    let metrics = demographicMetrics.filter(metric => (metric.inferenceSource?.type === 'DataLayers') === isAgs);

    if (metrics.length === 0 || (isAgs && !radiusFilterRequest && !polygonFilterRequest && !boundaryFilterRequest && !boundaryTerritoryFilterRequest)) {
      return [];
    }

    if (isAgs && validateDataLayerArea) {
      const areaValidation = await validateDataLayerArea();

      if (!areaValidation.isValid) {
        dataLayerAreaErrorCode = areaValidation.errorCode;
        return [];
      }

      const { supportedRegions } = areaValidation;
      if (supportedRegions) {
        metrics = metrics.filter(metric => isDataLayerMetricInRegions(metric, supportedRegions));
      }

      if (metrics.length === 0) {
        return [];
      }
    }

    const dataLayerSourceData = metrics.find(metric => metric.inferenceSource?.type === 'DataLayers')?.inferenceSource;
    const dataLayerRegion = dataLayerSourceData?.type === 'DataLayers' ? dataLayerSourceData.region : DEFAULT_DEMOGRAPHIC_REGION;

    const request = {
      map_id: mapId,
      demographics_ids: [...new Set(metrics.map(metric => metric.demographic.id))],
      radius_filter: radiusFilterRequest,
      polygon_filter: polygonFilterRequest,
      boundary_filter: boundaryFilterRequest,
      boundary_territory_filter: boundaryTerritoryFilterRequest,
    };
    const agsRequest = {
      ...request,
      region: dataLayerRegion,
    };

    if (!isAgs) {
      return getDemographicsQuery(clientId, request, cancelToken);
    }

    return getAgsDemographicsQuery(clientId, agsRequest, cancelToken)
      // show regular metrics despite an error on AGS endpoint/fail gracefully
      .catch((e) => {
        console.error(e);
        isAgsError = true;
        return [];
      });
  }));
  const response = responses.flat();

  const metrics = response.map((result): MetricProps | null => {
    const metric = demographicMetrics.find(demographicMetric => demographicMetric.demographic.id === result.demographicId);

    if (result.success && metric) {
      return {
        demographicId: result.demographicId,
        name: result.name,
        value: result.value,
        prefix: result.preData ?? undefined,
        suffix: result.postData ?? undefined,
        additionalData: [],
        inferenceSource: metric.inferenceSource,
      };
    }
    else {
      return null;
    }
  }).filter(notNull);

  return { metrics, isAgsError, dataLayerAreaErrorCode };
};

const getSpreadsheetColumnMetricsData = async ({ clientId, mapId, cancelToken, selectedMetrics, filterTreeParams,
  radiusFilterRequest, polygonFilterRequest, boundaryFilterRequest, boundaryTerritoryFilterRequest, spreadsheetRowsCount }: GetMetricsDataArguments): Promise<SpreadsheetMetricProps> => {
  const spreadsheetColumnMetrics = selectedMetrics
    .filter((metric): metric is SpreadsheetColumnMetricModel => metric.type === SelectedDataType.SpreadsheetColumn);

  const metricsSpreadsheetIds = selectedMetrics.reduce<Set<number>>((acc, metric) => {
    if (metric.type === SelectedDataType.SpreadsheetColumn) {
      acc.add(metric.spreadsheetColumn.spreadsheetId);
    }

    return acc;
  }, new Set());
  const filterTree: FilterTreeRequest = filterTreeParams ? createFilterTreeRequestFromMapSettings(filterTreeParams, []) : newPerSpreadsheetMap();

  const searchFilterTree: FilterTreeRequest = filterTreeParams?.search.selectedMatchingBehaviour === SearchMatchingBehaviour.ShowOnlyMatches
    ? createSearchFilterTreeRequestFromMapSettings(filterTreeParams, Array.from(metricsSpreadsheetIds))
    : newPerSpreadsheetMap();

  const perSpreadsheetColumns: PerSpreadsheet<PerColumn<Set<DataType>>> = newPerSpreadsheetMap();

  const registerColumnDataType = ({
    columns, column, dataType,
  }: {
    columns: PerSpreadsheet<PerColumn<Set<DataType>>>;
    column: SpreadsheetColumn;
    dataType: DataType;
  }) => {
    const spreadSheetColumns = columns[column.spreadsheetId] ?? {};
    columns[column.spreadsheetId] = spreadSheetColumns;

    const columnsTypesToFetch = spreadSheetColumns[column.id] ?? new Set();
    spreadSheetColumns[column.id] = columnsTypesToFetch;

    columnsTypesToFetch.add(dataType);
  };

  // process spreadsheet metrics
  for (const metric of spreadsheetColumnMetrics) {
    if (numericMetricsTypes.includes(metric.dataAction)) {
      registerColumnDataType({ columns: perSpreadsheetColumns, column: metric.spreadsheetColumn, dataType: DataType.NUMBER });
    }

    if (perColumnGroupedMetric.includes(metric.dataAction)) {
      registerColumnDataType({ columns: perSpreadsheetColumns, column: metric.spreadsheetColumn, dataType: DataType.GROUP });
    }

    if (metric.dataAction === MetricsDataAction.GROUP_TOTAL) {
      registerColumnDataType({ columns: perSpreadsheetColumns, column: metric.secondarySpreadsheetColumn, dataType: DataType.NUMBER });
    }
  }

  const perSpreadsheetBulkDataGetters: PerSpreadsheet<SpreadsheetDataBulkRequestGetter> = newPerSpreadsheetMap();

  const perSpreadsheetColumnsEntries = Object.entries(perSpreadsheetColumns)
    .map(([spreadsheetId, columns]) => [Number(spreadsheetId), columns] as const)
    // Add rows count request only when there is no other request from the same spreadsheet to ensure a request is made even when there are no column metrics.
    .concat(spreadsheetRowsCount
      ?.filter(spreadsheetId => !perSpreadsheetColumns[spreadsheetId])
      .map(spreadsheetId => [spreadsheetId, {}] as const) ?? []
    );

  for (const [spreadsheetId, columns] of perSpreadsheetColumnsEntries) {

    const columnsToFetchMap = Object.entries(columns)
      .reduce<DeepWritable<SpreadsheetDataColumnsToFetch>>((acc, [columnId, dataTypes]) => {
        dataTypes.forEach(type => {
          acc[type] = acc[type] ?? {};
          acc[type][columnId] = {
            empty_is_null: true,
          };
        });

        return acc;
      }, {});

    const columnsToFetch = Object.keys(columnsToFetchMap).length === 0 ? undefined : columnsToFetchMap;

    const filterTreeRequest = filterTree[spreadsheetId]?.filterTree;
    const searchFilterTreeRequest = searchFilterTree[spreadsheetId]?.filterTree;

    const mergedFilterTrees = mergeFilterTrees([filterTreeRequest, searchFilterTreeRequest], 'and');

    if (!perSpreadsheetBulkDataGetters[spreadsheetId]) {
      perSpreadsheetBulkDataGetters[spreadsheetId] = {
        map_id: mapId,
        spreadsheet_id: spreadsheetId,
        exclude_basic_data: true,
        exclude_row_data: false,
        columns_to_fetch: columnsToFetch,
        filter: {
          only_get_filtered: true,
          filter_tree: mergedFilterTrees ?? undefined,
          radius_filter: radiusFilterRequest,
          polygon_filter: polygonFilterRequest,
          boundary_filter: boundaryFilterRequest,
          boundary_territory_filter: boundaryTerritoryFilterRequest,
        },
      };
    }
  }

  const bulkDataGetters: SpreadsheetDataBulkRequestGetter[] = [];

  Object.keys(perSpreadsheetBulkDataGetters).forEach(spreadsheetIdString => {
    const spreadsheetId = +spreadsheetIdString;
    const spreadsheetGetter = perSpreadsheetBulkDataGetters[spreadsheetId];

    if (spreadsheetGetter) {
      bulkDataGetters.push(spreadsheetGetter);
    }
  });

  if (bulkDataGetters.length === 0) {
    return {
      rowsCount: null,
      metricList: [],
    };
  }

  const spreadsheetColumnMetricsResults = await getSpreadsheetBulkData(clientId, { params: bulkDataGetters }, cancelToken);

  const metricList = spreadsheetColumnMetrics.reduce<MetricProps[]>((acc, metric) => {
    let value: number;
    const additionalData: Array<{ name: string; value: number }> = [];

    // group count / group total
    if (perColumnGroupedMetric.includes(metric.dataAction)) {
      const isGroupTotal = metric.dataAction === MetricsDataAction.GROUP_TOTAL;
      value = 0;
      spreadsheetColumnMetricsResults.data.forEach(response => {
        if (!isSuccessBulkSpreadsheet(response) || response.spreadsheet_id !== metric.spreadsheetColumn.spreadsheetId) {
          return;
        }

        const columnValues = response.result.columns_to_fetch?.[DataType.GROUP]?.[metric.spreadsheetColumn.id];
        if (!columnValues) {
          return;
        }

        const secondaryColumnValues = isGroupTotal && metric.secondarySpreadsheetColumn
          ? response.result.columns_to_fetch?.[DataType.NUMBER]?.[metric.secondarySpreadsheetColumn.id]
          : undefined;

        const additionalColumnAggregates: Array<{name: string; total: number}> =
          (columnValues.unique_values_formatted || columnValues.unique_values).map((name) => ({
            name,
            total: 0,
          }));

        Object.keys(columnValues.row_values).forEach(rowId => {
          const itemUniqueGroupIndex = columnValues.row_values[rowId];

          if (itemUniqueGroupIndex === null || itemUniqueGroupIndex === undefined) {
            return;
          }

          const item = additionalColumnAggregates[itemUniqueGroupIndex];
          if (!item) {
            return;
          }

          if (secondaryColumnValues) {
            const rowValue = secondaryColumnValues.row_values[rowId];
            if (!isNullOrUndefined(rowValue)) {
              item.total += rowValue;
            }
          }
          else {
            item.total++;
          }
        });

        const additionalColumnAggregatesWithValues = additionalColumnAggregates
          .filter(item => item.total > 0)
          .sort((a, b) => b.total - a.total);

        value = isGroupTotal
          ? additionalColumnAggregatesWithValues.reduce((sum, item) => sum + item.total, 0)
          : additionalColumnAggregatesWithValues.length;

        additionalData.push(...additionalColumnAggregatesWithValues.map(item => ({
          name: item.name,
          value: item.total,
        })));
      });
    }
    // numeric results
    else {
      const fallBackValue = 0;
      value = fallBackValue;

      spreadsheetColumnMetricsResults.data.forEach(response => {
        if (!isSuccessBulkSpreadsheet(response) || response.spreadsheet_id !== metric.spreadsheetColumn.spreadsheetId) {
          return;
        }

        const columnValues = response.result.columns_to_fetch?.[DataType.NUMBER]?.[metric.spreadsheetColumn.id];
        if (!columnValues || !isColumnNumericFromNumberResponse(metric.spreadsheetColumn.id, columnValues)) {
          return;
        }

        const columnValuesKeys = Object.keys(columnValues.row_values);
        let valuesCount = 0;
        let sum = 0;
        let lowValue: number | null = null;
        let highValue: number | null = null;

        columnValuesKeys.forEach(rowId => {
          const rowValue = columnValues.row_values[rowId];

          if (isNullOrUndefined(rowValue)) {
            return;
          }

          valuesCount++;
          sum += rowValue;
          if (lowValue === null || rowValue < lowValue) {
            lowValue = rowValue;
          }

          if (highValue === null || rowValue > highValue) {
            highValue = rowValue;
          }
        });

        switch (metric.dataAction) {
          case MetricsDataAction.AVERAGE:
            value = valuesCount === 0 ? fallBackValue : (sum / valuesCount);
            additionalData.push({
              name: METRICS_EXCLUDED_ROWS_COUNT,
              value: columnValuesKeys.length - valuesCount,
            });
            additionalData.push({
              name: METRICS_ROWS_COUNT,
              value: columnValuesKeys.length,
            });
            break;
          case MetricsDataAction.LOW_VALUE:
            value = lowValue ?? fallBackValue;
            break;
          case MetricsDataAction.HIGH_VALUE:
            value = highValue ?? fallBackValue;
            break;
          case MetricsDataAction.SUM: {
            value = sum;
            break;
          }
          default:
            break;
        }
      });
    }

    acc.push({
      additionalData,
      dataAction: metric.dataAction,
      secondaryColumnName: metric.dataAction === MetricsDataAction.GROUP_TOTAL ? metric.secondarySpreadsheetColumn.name : undefined,
      name: metric.spreadsheetColumn.name,
      value,
      inferenceSource: metric.inferenceSource,
    });

    return acc;
  }, []);

  const rowsCount = spreadsheetColumnMetricsResults.data.reduce((accCount, response) => {
    if (!isSuccessBulkSpreadsheet(response)) {
      return accCount;
    }

    return accCount + (response.result.filtered_rows?.length ?? 0);
  }, 0);

  return {
    rowsCount,
    metricList,
  };
};
